import { useState, useEffect } from "react";
import { AnimatePresence, motion } from "framer-motion";
import { X, Plus, Gift, Heart } from "lucide-react";
import { useCart } from "../context/CartContext";
import { useWishlist } from "../context/WishlistContext";
import { money } from "../lib/format";

export default function ProductModal({ product, currency, onClose }) {
  const { addItem } = useCart();
  const { has, toggle } = useWishlist();
  const [active, setActive] = useState(0);

  const gallery =
    product && product.images && product.images.length
      ? product.images
      : product
      ? [product.image]
      : [];

  useEffect(() => {
    setActive(0);
  }, [product]);

  const hasOffer = product?.is_offer && product?.offer_price;

  return (
    <AnimatePresence>
      {product && (
        <>
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            onClick={onClose}
            className="fixed inset-0 z-[85] bg-espresso/50 backdrop-blur-sm"
          />
          <motion.div
            data-testid="product-modal"
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: 30 }}
            transition={{ duration: 0.4, ease: [0.22, 1, 0.36, 1] }}
            className="fixed inset-0 z-[86] flex items-center justify-center p-4 pointer-events-none"
          >
            <div className="bg-cream w-full max-w-4xl rounded-2xl overflow-hidden pointer-events-auto max-h-[92vh] flex flex-col md:flex-row">
              {/* Gallery */}
              <div className="md:w-1/2 bg-surface relative">
                <div className="aspect-square md:h-full overflow-hidden">
                  <AnimatePresence mode="wait">
                    <motion.img
                      key={active}
                      data-testid="product-modal-main-image"
                      src={gallery[active]}
                      alt={product.name}
                      initial={{ opacity: 0 }}
                      animate={{ opacity: 1 }}
                      exit={{ opacity: 0 }}
                      transition={{ duration: 0.3 }}
                      className="w-full h-full object-cover"
                    />
                  </AnimatePresence>
                </div>
                {gallery.length > 1 && (
                  <div className="absolute bottom-3 left-1/2 -translate-x-1/2 flex gap-2 bg-cream/80 backdrop-blur px-2 py-2 rounded-full">
                    {gallery.map((g, i) => (
                      <button
                        key={i}
                        data-testid={`product-thumb-${i}`}
                        onClick={() => setActive(i)}
                        className={`w-10 h-10 rounded-full overflow-hidden border-2 transition-colors ${
                          i === active ? "border-rose" : "border-transparent"
                        }`}
                      >
                        <img src={g} alt="" className="w-full h-full object-cover" />
                      </button>
                    ))}
                  </div>
                )}
              </div>

              {/* Details */}
              <div className="md:w-1/2 p-6 md:p-10 flex flex-col overflow-y-auto">
                <div className="flex justify-between items-start">
                  <div className="flex flex-wrap gap-2">
                    {hasOffer && (
                      <span className="bg-rose text-cream text-[10px] uppercase tracking-[0.15em] px-3 py-1 rounded-full">
                        Oferta
                      </span>
                    )}
                    {product.is_giftset && (
                      <span className="inline-flex items-center gap-1 bg-espresso text-cream text-[10px] uppercase tracking-[0.15em] px-3 py-1 rounded-full">
                        <Gift size={11} /> Set de regalo
                      </span>
                    )}
                  </div>
                  <button
                    data-testid="product-modal-close"
                    onClick={onClose}
                    className="text-espresso hover:text-rose transition-colors"
                  >
                    <X size={22} />
                  </button>
                </div>

                <h2 className="font-serif text-4xl md:text-5xl mt-6 leading-none">
                  {product.name}
                </h2>

                <div className="mt-4 flex items-baseline gap-3">
                  {hasOffer ? (
                    <>
                      <span className="text-rose text-2xl font-medium">
                        {money(product.offer_price, currency)}
                      </span>
                      <span className="text-taupe line-through">
                        {money(product.price, currency)}
                      </span>
                    </>
                  ) : (
                    <span className="text-espresso text-2xl font-medium">
                      {money(product.price, currency)}
                    </span>
                  )}
                </div>

                <p className="text-taupe font-light leading-relaxed mt-6">
                  {product.description}
                </p>

                <div className="mt-auto pt-8">
                  {product.is_upcoming ? (
                    <p className="text-center text-taupe font-serif italic text-lg">
                      Disponible próximamente
                    </p>
                  ) : (
                    <div className="flex gap-3">
                      <button
                        data-testid="product-modal-add"
                        onClick={() => {
                          addItem(product);
                          onClose();
                        }}
                        className="flex-1 inline-flex items-center justify-center gap-3 bg-espresso text-cream rounded-full py-4 text-xs uppercase tracking-[0.2em] hover:bg-rose transition-colors"
                      >
                        <Plus size={16} /> Agregar al carrito
                      </button>
                      <button
                        data-testid="product-modal-wishlist"
                        onClick={() => toggle(product)}
                        className="w-14 shrink-0 rounded-full border border-line flex items-center justify-center hover:border-rose transition-colors"
                        aria-label="Guardar en favoritos"
                      >
                        <Heart
                          size={18}
                          className={has(product.id) ? "fill-rose text-rose" : "text-espresso"}
                        />
                      </button>
                    </div>
                  )}
                </div>
              </div>
            </div>
          </motion.div>
        </>
      )}
    </AnimatePresence>
  );
}
